import { Box, Button, HStack, IconButton, Portal, Spinner, Stack, Text, Badge } from "@chakra-ui/react";
import { useState } from "react";
import { FiChevronUp, FiMinus, FiX } from "react-icons/fi";
import {
  useActiveCampaign,
  useCancelCampaign,
  useDismissCampaign,
  usePauseCampaign,
  useResumeCampaign,
  type ActiveCampaignResponse,
} from "@/hooks/useCampaign";

export default function CampaignBatchModal() {
  const { data: batch } = useActiveCampaign();
  const cancel = useCancelCampaign();
  const dismiss = useDismissCampaign();
  const pause = usePauseCampaign();
  const resume = useResumeCampaign();
  const [minimised, setMinimised] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  if (!batch) return null;

  const isActive = batch.status === "RUNNING" || batch.status === "CANCELLING";
  const pct =
    batch.totalClients > 0 ? Math.round((batch.done / batch.totalClients) * 100) : 0;

  if (minimised) {
    return (
      <Portal>
        <Box
          position="fixed"
          top={20}
          left="50%"
          transform="translateX(-50%)"
          zIndex={1400}
          bg="blue.600"
          color="white"
          borderRadius="full"
          px={4}
          py={2}
          shadow="lg"
          display="flex"
          alignItems="center"
          gap={2}
          cursor="pointer"
          onClick={() => setMinimised(false)}
          _hover={{ bg: "blue.700" }}
          transition="background 0.15s"
          userSelect="none"
        >
          <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
            {batch.status === "CANCELLING"
              ? "Cancelling campaign…"
              : batch.status === "COMPLETED"
              ? `Campaign complete — ${pct}%`
              : batch.status === "CANCELLED"
              ? "Campaign cancelled"
              : `Calling In Progress ${pct}%`}
          </Text>
          <FiChevronUp size={14} />
        </Box>
      </Portal>
    );
  }

  return (
    <Portal>
      {isActive && (
        <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1300} />
      )}
      <Box
        position="fixed"
        inset={0}
        zIndex={1400}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
        pointerEvents={isActive ? undefined : "none"}
      >
        <Box
          bg="bg.panel"
          borderRadius="xl"
          borderWidth="1px"
          maxW="md"
          w="full"
          shadow="2xl"
          overflow="hidden"
          pointerEvents="auto"
        >
          <HStack
            px={5}
            pt={5}
            pb={4}
            borderBottomWidth="1px"
            justify="space-between"
            align="center"
          >
            <HStack gap={2}>
              {batch.status === "RUNNING" && <Spinner size="sm" color="blue.400" />}
              {batch.status === "CANCELLING" && <Spinner size="sm" color="orange.400" />}
              <Text fontWeight="semibold" fontSize="lg">
                {batch.status === "RUNNING"
                  ? "Campaign Running"
                  : batch.status === "CANCELLING"
                  ? "Cancelling…"
                  : batch.status === "COMPLETED"
                  ? "Campaign Complete"
                  : "Campaign Cancelled"}
              </Text>
              <Badge
                colorPalette={batch.mode === "SIMULATION" ? "purple" : "blue"}
                variant="subtle"
                fontSize="xs"
              >
                {batch.mode}
              </Badge>
            </HStack>
            <HStack gap={1}>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Minimise"
                onClick={() => setMinimised(true)}
              >
                <FiMinus />
              </IconButton>
              {/* Dismiss (X) is only valid for terminal batches — active
                  ones must be cancelled first via the Stop Campaign button. */}
              {!isActive && (
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Dismiss"
                  loading={dismiss.isPending}
                  onClick={() => dismiss.mutate(batch.id)}
                >
                  <FiX />
                </IconButton>
              )}
            </HStack>
          </HStack>

          <Stack px={5} py={5} gap={5}>
            <Stack gap={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" color="fg.muted">
                  {batch.done} of {batch.totalClients} clients
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {pct}%
                </Text>
              </HStack>
              <Box h="6px" bg="bg.subtle" borderRadius="full" overflow="hidden">
                <Box
                  h="full"
                  bg={
                    batch.status === "CANCELLING"
                      ? "orange.400"
                      : batch.status === "COMPLETED"
                      ? "green.500"
                      : batch.status === "CANCELLED"
                      ? "gray.400"
                      : "blue.500"
                  }
                  borderRadius="full"
                  style={{ width: `${pct}%`, transition: "width 0.4s ease" }}
                />
              </Box>
            </Stack>

            <StatsRow batch={batch} />

            {batch.pausedAt && (
              <Stack
                gap={2}
                p={3}
                borderRadius="md"
                bg="orange.50"
                _dark={{ bg: "orange.900" }}
                borderWidth="1px"
                borderColor="orange.300"
              >
                <Text fontSize="sm" fontWeight="medium" color="orange.700" _dark={{ color: "orange.200" }}>
                  Campaign paused
                </Text>
                <Text fontSize="xs" color="orange.700" _dark={{ color: "orange.300" }}>
                  {batch.pauseReason ??
                    "Too many recent calls returned a technical error."}
                </Text>
                <Button
                  size="sm"
                  colorPalette="orange"
                  loading={resume.isPending}
                  onClick={() => resume.mutate(batch.id)}
                  alignSelf="flex-start"
                >
                  Resume
                </Button>
              </Stack>
            )}

            {isActive && (
              <Text fontSize="xs" color="fg.muted">
                {batch.pending} pending · {batch.dispatched} in progress
              </Text>
            )}

            {isActive && (
              <Stack gap={2}>
                {confirmCancel ? (
                  <Stack gap={2}>
                    <Text fontSize="sm" color="red.400" fontWeight="medium">
                      Stop calling remaining clients? Calls already in progress will
                      finish naturally.
                    </Text>
                    <HStack gap={2}>
                      <Button
                        size="sm"
                        colorPalette="red"
                        loading={cancel.isPending}
                        disabled={batch.status === "CANCELLING"}
                        onClick={() => {
                          cancel.mutate(batch.id);
                          setConfirmCancel(false);
                        }}
                      >
                        Yes, stop campaign
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmCancel(false)}
                      >
                        Keep going
                      </Button>
                    </HStack>
                  </Stack>
                ) : (
                  <HStack gap={2}>
                    {/* Manual pause: keeps PENDING rows queued. Only
                        shows when RUNNING and not already paused (the
                        existing pausedAt banner above handles Resume). */}
                    {batch.status === "RUNNING" && !batch.pausedAt && (
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="orange"
                        loading={pause.isPending}
                        onClick={() => pause.mutate(batch.id)}
                      >
                        Pause
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="red"
                      disabled={batch.status === "CANCELLING"}
                      onClick={() => setConfirmCancel(true)}
                    >
                      Stop Campaign
                    </Button>
                  </HStack>
                )}
              </Stack>
            )}

          </Stack>
        </Box>
      </Box>
    </Portal>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Stack
      gap={0}
      flex="1"
      minW="4rem"
      bg="bg.subtle"
      borderRadius="md"
      p={3}
      align="center"
    >
      <Text fontSize="xl" fontWeight="bold" color={color} lineHeight="1.2">
        {value}
      </Text>
      <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
        {label}
      </Text>
    </Stack>
  );
}

function StatsRow({ batch }: { batch: ActiveCampaignResponse }) {
  return (
    <HStack gap={2} wrap="wrap">
      <StatCard label="Booked" value={batch.outcomes.BOOKED} color="green.500" />
      <StatCard
        label="No Answer"
        value={batch.outcomes.NO_ANSWER + batch.outcomes.BUSY}
        color="orange.400"
      />
      <StatCard
        label="Declined"
        value={
          batch.outcomes.DECLINED_OK_TO_RECALL +
          batch.outcomes.DECLINED_DO_NOT_CALL
        }
        color="yellow.500"
      />
      <StatCard label="Failed" value={batch.outcomes.FAILED} color="red.400" />
      {batch.outcomes.TECHNICAL_ERROR > 0 && (
        <StatCard
          label="Tech Errors"
          value={batch.outcomes.TECHNICAL_ERROR}
          color="purple.400"
        />
      )}
      {batch.skipped > 0 && (
        <StatCard label="Skipped" value={batch.skipped} color="fg.muted" />
      )}
    </HStack>
  );
}
